'use server';

import { langPrefix } from '@/lib/locale-path';
import { mailConfigured } from '@/lib/mail-accounts';
import { publicError } from '@/lib/action-errors';
import { query, transaction } from '@/lib/db';
import { getSession, setSessionCookie, clearSessionCookie, startUserSession, UserSession } from '@/lib/session';
import { sendEmail } from '@/lib/email';
import { hashPassword, verifyPassword } from '@/lib/auth-passwords';
import { tryGetSecret } from '@/lib/secrets';
import {
  issueEmailCode,
  consumeEmailCode,
  normalizeEmail,
  isValidEmail,
  PendingRegistration,
  IssueCodeResult,
} from '@/lib/email-code';
import crypto from 'crypto';
import { headers } from 'next/headers';
import { generateAgencySlug } from '@/lib/agency-slug-db';
import { rateLimit, countHits } from '@/lib/rate-limit';
import { ipFromHeaders } from '@/lib/request-ip';
import { isIpBanned } from '@/lib/banned-ips';
import { startRegistrationBinding, readRegistrationBinding, clearRegistrationBinding } from '@/lib/registration-binding';
import { notFasterThan, burnPasswordVerification, LOGIN_FLOOR_MS, RESET_FLOOR_MS } from '@/lib/uniform-timing';
import { isMemberRole, findMembership, type MemberRole, type AgencyRole } from '@/lib/agency-access';
import { revalidatePath } from 'next/cache';
import { removeUnreferencedUploads } from '@/lib/uploads-cleanup';
import { findWaitingBuyers, tellBuyersListingWithdrawn } from '@/lib/listing-withdrawn';

const MIN_PASSWORD_LENGTH = 8;
const MAX_FAILED_LOGINS = 5;
const LOCK_MINUTES = 15;
const HOUR_MS = 60 * 60 * 1000;

async function clientIp(): Promise<string> {
  const h = await headers();
  return ipFromHeaders((name) => h.get(name));
}

// Per-address limits stop one account being hammered; these per-connection
// limits stop the same script trying one password across many accounts, and
// stop anyone using our mail server to bombard a stranger's inbox.
async function throttle(bucket: string, max: number, windowMs = HOUR_MS): Promise<boolean> {
  const ip = await clientIp();
  if (await isIpBanned(ip)) return false;
  return (await rateLimit(`${bucket}:${ip}`, max, windowMs)).ok;
}

export type AuthResult =
  // agency_member: the person is on an agency's team (an invited manager or consultant has role "user",
  // so the role alone sends them to the seller area instead of the cabinet).
  | { success: true; role: UserSession['role']; is_realtor: boolean; agency_status: string; agency_member?: boolean }
  | { success: false; error: string; needsVerification?: false; cooldownSeconds?: number }
  // codeProblem / attemptsLeft: what is wrong with a typed code, as data, so the screen says it in its own
  // words (the text of the error itself never reaches the screen unless it is in the catalogue).
  | { success: false; needsVerification: true; email: string; error?: string; cooldownSeconds?: number; codeProblem?: 'invalid' | 'expired' | 'too_many'; attemptsLeft?: number };

function t(lang: string, en: string, fr: string) {
  return lang === 'fr' ? fr : en;
}

function cooldownMessage(lang: string, seconds: number) {
  return t(
    lang,
    `A code was sent recently. You can request a new one in ${seconds} s.`,
    `Un code vient d'être envoyé. Vous pourrez en demander un nouveau dans ${seconds} s.`
  );
}

// An administrator who signs in with a code, not a password, gets the shorter session
// the password sign-in gives: the code proves the mailbox and nothing more.
async function signedInResult(user: any, durationDays = 30): Promise<AuthResult> {
  await startUserSession(user, durationDays);
  const member = user.role !== 'admin' && !user.is_realtor && user.id ? Boolean(await findMembership(user.id)) : false;
  return {
    success: true,
    role: user.role === 'admin' ? 'admin' : user.is_realtor ? 'realtor' : 'user',
    is_realtor: Boolean(user.is_realtor),
    agency_status: user.agency_status,
    agency_member: member,
  };
}

const USER_COLUMNS = `id, email, name, role, is_realtor, is_blocked, block_reason, agency_status,
  password_hash, session_version, failed_login_count, locked_until, deleted_at`;

async function findUserByEmail(email: string) {
  const res = await query(`SELECT ${USER_COLUMNS} FROM users WHERE LOWER(email) = LOWER($1)`, [email]);
  return res.rows[0] || null;
}

function blockedMessage(lang: string, user: any) {
  return t(
    lang,
    `Account suspended: ${user.block_reason || 'please contact administration.'}`,
    `Compte suspendu : ${user.block_reason || 'veuillez contacter le support.'}`
  );
}

// Environment admin credentials (ADMIN_PASSWORD). Without the variable admin
// sign-in is disabled.
//
// The address it belongs to is fixed: it signs in only as this one, never as
// whatever address the form was given.
const ENVIRONMENT_ADMIN_EMAIL = 'admin@realestatechannel.ca';

// Failures are counted in the database, per connection and overall. They used to
// live in a variable of the server process, which a restart or a second instance
// forgot, and which anybody could fill to lock the real administrator out: a
// failure now locks out only the connection that made it.
const ADMIN_ENV_GLOBAL_FAILURES_PER_HOUR = 40;

async function checkAdminPassword(password: string): Promise<'ok' | 'invalid' | 'locked' | 'disabled'> {
  const secret = tryGetSecret('ADMIN_PASSWORD');
  if (!secret) return 'disabled';

  const ip = await clientIp();
  const perConnection = `admin-env-fail:${ip}`;
  if (
    (await countHits(perConnection, LOCK_MINUTES * 60 * 1000)) >= MAX_FAILED_LOGINS ||
    (await countHits('admin-env-fail:all', HOUR_MS)) >= ADMIN_ENV_GLOBAL_FAILURES_PER_HOUR
  ) {
    return 'locked';
  }

  const a = crypto.createHash('sha256').update(password).digest();
  const b = crypto.createHash('sha256').update(secret).digest();
  if (crypto.timingSafeEqual(a, b)) return 'ok';

  await rateLimit(perConnection, MAX_FAILED_LOGINS, LOCK_MINUTES * 60 * 1000);
  await rateLimit('admin-env-fail:all', ADMIN_ENV_GLOBAL_FAILURES_PER_HOUR, HOUR_MS);
  return 'invalid';
}

// One statement counts the failure and, at the limit, sets the lock. Reading the
// count and writing count + 1 afterwards let parallel guesses each see the same
// number, so the lock could arrive well after the fifth wrong password.
async function recordFailedLogin(userId: string) {
  const res = await query(
    `UPDATE users SET
       failed_login_count = CASE WHEN COALESCE(failed_login_count, 0) + 1 >= $2 THEN 0 ELSE COALESCE(failed_login_count, 0) + 1 END,
       locked_until = CASE WHEN COALESCE(failed_login_count, 0) + 1 >= $2
                           THEN NOW() + ($3::int * INTERVAL '1 minute') ELSE locked_until END
     WHERE id = $1
     RETURNING email, locked_until`,
    [userId, MAX_FAILED_LOGINS, LOCK_MINUTES]
  );
  // The lock answers even the right password like a wrong one (so a stranger learns nothing), which leaves its
  // owner puzzled: tell them, by email, once, at the moment it starts.
  const row = res.rows[0];
  const startedNow = row?.locked_until && new Date(row.locked_until).getTime() - Date.now() > (LOCK_MINUTES - 1) * 60_000;
  if (startedNow && row.email) {
    try {
      const appUrl = process.env.NEXT_PUBLIC_APP_URL || process.env.APP_URL || 'http://localhost:3000';
      await sendEmail({
        to: row.email,
        subject: `Sign-in paused for ${LOCK_MINUTES} minutes / Connexion suspendue ${LOCK_MINUTES} minutes`,
        text: `There were ${MAX_FAILED_LOGINS} wrong passwords for your account, so sign-in is paused for ${LOCK_MINUTES} minutes (even with the right password). If it was you, wait, or reset your password: ${appUrl}/restore-access. If it was not you, nothing was opened and your password is unchanged.

Il y a eu ${MAX_FAILED_LOGINS} mots de passe incorrects pour votre compte : la connexion est suspendue ${LOCK_MINUTES} minutes (même avec le bon mot de passe). Si c’était vous, patientez ou réinitialisez votre mot de passe : ${appUrl}/fr/restore-access. Sinon, rien n’a été ouvert et votre mot de passe n’a pas changé.`,
      });
    } catch (error) {
      console.error('Could not send the lock notice:', error);
    }
  }
}

export async function getCurrentUserSession(): Promise<(UserSession & { agencyRole?: AgencyRole }) | null> {
  const session = await getSession();
  if (!session) return null;
  // The role of somebody on an agency's team, so the menu can offer the cabinet:
  // an invited manager or consultant has the account role "user".
  if (session.role !== 'admin' && session.userId) {
    const membership = await findMembership(session.userId);
    if (membership) return { ...session, agencyRole: membership.role };
  }
  return session;
}

export async function logoutUserAction() {
  await clearSessionCookie();
  return { success: true };
}

/**
 * Sends a one-time sign-in code. Used for passwordless sign-in and to confirm the
 * email address before a listing is published.
 */
export async function requestEmailCodeAction(email: string, lang = 'en'): Promise<AuthResult> {
  const cleanEmail = normalizeEmail(email);
  if (!isValidEmail(cleanEmail)) {
    return { success: false, error: t(lang, 'Invalid email address.', 'Adresse courriel invalide.') };
  }

  // Each code is an email we send on request, so the request itself is limited.
  if (!(await throttle('code', 15))) {
    return {
      success: false,
      error: t(lang, 'Too many requests. Please try again later.', 'Trop de demandes. Réessayez plus tard.'),
    };
  }

  // A code goes to the mailbox whatever the account is: whether it exists, or is
  // blocked, is told only to somebody who then enters it (verifyEmailCodeAction).
  return codeStepResult(lang, cleanEmail, await issueEmailCode(cleanEmail, lang, null));
}

// Maps the outcome of sending a code to what the form shows next.
function codeStepResult(lang: string, email: string, issued: IssueCodeResult, notice?: string): AuthResult {
  if (issued.ok) {
    return { success: false, needsVerification: true, email, error: notice };
  }
  if (issued.reason === 'cooldown') {
    return {
      success: false,
      needsVerification: true,
      email,
      cooldownSeconds: issued.cooldownSeconds,
      error: notice || cooldownMessage(lang, issued.cooldownSeconds),
    };
  }
  return {
    success: false,
    error: t(
      lang,
      'We could not send the confirmation email. Please try again in a few minutes.',
      "Nous n'avons pas pu envoyer le courriel de confirmation. Réessayez dans quelques minutes."
    ),
  };
}

/**
 * Confirms a code. Creates the account on first confirmation (applying any pending
 * sign-up details) and starts the session.
 */
export async function verifyEmailCodeAction(email: string, code: string, lang = 'en'): Promise<AuthResult> {
  const cleanEmail = normalizeEmail(email);

  // Guessing a code is guessing a password of six digits, so it is limited by the
  // connection here and by the address inside consumeEmailCode.
  if (!(await throttle('verify', 40))) {
    return {
      success: false,
      needsVerification: true,
      email: cleanEmail,
      error: t(lang, 'Too many attempts. Please try again later.', 'Trop de tentatives. Réessayez plus tard.'),
    };
  }

  const consumed = await consumeEmailCode(cleanEmail, code, await readRegistrationBinding());

  if (!consumed.ok) {
    const error =
      consumed.reason === 'expired'
        ? t(lang, 'This code has expired. Request a new one.', 'Ce code a expiré. Demandez-en un nouveau.')
        : consumed.reason === 'too_many_attempts'
          ? t(lang, 'Too many attempts. Request a new code.', 'Trop de tentatives. Demandez un nouveau code.')
          : consumed.attemptsLeft != null && consumed.attemptsLeft > 0
            ? t(lang, `Incorrect code. ${consumed.attemptsLeft} ${consumed.attemptsLeft === 1 ? 'attempt' : 'attempts'} left.`, `Code incorrect. Il vous reste ${consumed.attemptsLeft} ${consumed.attemptsLeft === 1 ? 'essai' : 'essais'}.`)
            : t(lang, 'Incorrect code.', 'Code incorrect.');
    return {
      success: false,
      needsVerification: true,
      email: cleanEmail,
      error,
      codeProblem: consumed.reason === 'expired' ? 'expired' : consumed.reason === 'too_many_attempts' ? 'too_many' : 'invalid',
      attemptsLeft: consumed.attemptsLeft,
    };
  }

  const pending: PendingRegistration = consumed.payload || {};
  if (pending.binding) await clearRegistrationBinding();
  let user = await findUserByEmail(cleanEmail);

  if (user?.is_blocked) {
    return { success: false, error: blockedMessage(lang, user) };
  }
  if (user?.deleted_at) {
    return {
      success: false,
      error: t(lang, 'This account has been deleted. Please contact administration.', 'Ce compte a été supprimé. Veuillez contacter le support.'),
    };
  }

  if (!user) {
    const isRealtor = pending.role === 'realtor';
    const agencySlug = isRealtor ? await generateAgencySlug(pending.name, cleanEmail.split('@')[0]) : null;
    const inserted = await query(
      `INSERT INTO users (email, password_hash, role, is_realtor, name, phone, city, province, agency_status, agency_slug, created_at)
       VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, NOW())
       RETURNING ${USER_COLUMNS}`,
      [
        cleanEmail,
        pending.password_hash || null,
        isRealtor ? 'realtor' : 'user',
        isRealtor,
        pending.name || null,
        pending.phone || null,
        pending.city || null,
        pending.province || null,
        isRealtor ? 'pending' : 'approved',
        agencySlug,
      ]
    );
    user = inserted.rows[0];
  } else {
    // The mailbox owner just proved control of the address: clear any lockout.
    const updated = await query(
      `UPDATE users
       SET failed_login_count = 0,
           locked_until = NULL,
           password_hash = COALESCE(password_hash, $2)
       WHERE id = $1
       RETURNING ${USER_COLUMNS}`,
      [user.id, pending.password_hash || null]
    );
    user = updated.rows[0];
  }

  return signedInResult(user, user.role === 'admin' ? 7 : 30);
}

/**
 * Standard Email + Password Login
 */
export async function loginWithPasswordAction(email: string, password: string, lang = 'en'): Promise<AuthResult> {
  // Every refusal leaves by the same door, after the same time. Without this,
  // an unknown address answered in the time of one query while a known one
  // answered after PBKDF2 at 100,000 iterations, and the difference told
  // anybody who timed it which addresses are registered here.
  const startedAt = Date.now();
  const refuse = <T>(result: T) => notFasterThan(startedAt, LOGIN_FLOOR_MS, result);

  const cleanEmail = normalizeEmail(email);
  if (!isValidEmail(cleanEmail) || !password) {
    return { success: false, error: t(lang, 'Email or password missing.', 'Courriel ou mot de passe manquant.') };
  }

  // The per-account lockout below does not stop one password being tried across
  // many accounts, so attempts from one connection are capped as well.
  if (!(await throttle('login', 30))) {
    return { success: false, error: t(lang, 'Too many attempts. Try again later.', 'Trop de tentatives. Réessayez plus tard.') };
  }

  // The same wording whether or not the address has an account, so the form
  // cannot be used to find out who is registered here.
  const wrongCredentials = {
    success: false as const,
    error: t(
      lang,
      'Incorrect email or password. If you do not have an account yet, create one.',
      "Courriel ou mot de passe incorrect. Si vous n'avez pas encore de compte, créez-en un."
    ),
  };

  const user = await findUserByEmail(cleanEmail);

  // Break-glass administrator from the environment: it has no users row, and it
  // works only when no real admin account owns that address.
  if (!user && cleanEmail === ENVIRONMENT_ADMIN_EMAIL) {
    const check = await checkAdminPassword(password);
    if (check === 'ok') {
      await setSessionCookie({ email: cleanEmail, role: 'admin', name: 'System Administrator' }, 7);
      return { success: true, role: 'admin', is_realtor: false, agency_status: 'approved' };
    }
    if (check === 'locked') {
      return { success: false, error: t(lang, 'Too many attempts. Try again later.', 'Trop de tentatives. Réessayez plus tard.') };
    }
    return wrongCredentials;
  }

  if (!user || user.deleted_at) {
    // Do the work a real verification would, so the answer is not quicker for
    // an address nobody has registered.
    burnPasswordVerification(password);
    return refuse(wrongCredentials);
  }

  // A suspended or locked account answers exactly like a wrong password until the
  // right one is given. Anything else tells a stranger that the address is
  // registered, and the reason for a suspension is for its owner alone.
  if (user.is_blocked) {
    if (user.password_hash && verifyPassword(password, user.password_hash)) {
      return refuse({ success: false as const, error: blockedMessage(lang, user) });
    }
    burnPasswordVerification(password);
    return refuse(wrongCredentials);
  }

  if (user.locked_until && new Date(user.locked_until).getTime() > Date.now()) {
    burnPasswordVerification(password);
    return refuse(wrongCredentials);
  }

  // Accounts created from a listing have no password: confirm by email code instead.
  if (!user.password_hash) {
    return codeStepResult(
      lang,
      cleanEmail,
      await issueEmailCode(cleanEmail, lang, null),
      t(
        lang,
        'This account has no password yet. Enter the code we sent to your email.',
        "Ce compte n'a pas encore de mot de passe. Saisissez le code envoyé à votre courriel."
      )
    );
  }

  if (!verifyPassword(password, user.password_hash)) {
    await recordFailedLogin(user.id);
    return refuse(wrongCredentials);
  }

  if (user.failed_login_count || user.locked_until) {
    await query(`UPDATE users SET failed_login_count = 0, locked_until = NULL WHERE id = $1`, [user.id]);
  }
  return signedInResult(user);
}

/**
 * Email + Password Registration. The account is created only after the emailed
 * code is confirmed, so nobody can register an address they do not control.
 */
export async function registerWithPasswordAction(data: {
  email: string;
  password: string;
  role: 'user' | 'realtor';
  name?: string;
  phone?: string;
  city?: string;
  province?: string;
  lang?: string;
}): Promise<AuthResult> {
  // Server actions take whatever the browser sends: a missing object, or a password that is not text, is refused like a bad one.
  if (!data || typeof data !== 'object') return { success: false, error: 'Invalid request.' };
  if (typeof data.password !== 'string') data = { ...data, password: '' };
  const lang = typeof data.lang === 'string' && data.lang ? data.lang : 'en';
  const cleanEmail = normalizeEmail(data.email);

  if (!isValidEmail(cleanEmail)) {
    return { success: false, error: t(lang, 'Invalid email address.', 'Courriel invalide.') };
  }
  if (!data.password || data.password.length < MIN_PASSWORD_LENGTH) {
    return {
      success: false,
      error: t(
        lang,
        `Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`,
        `Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`
      ),
    };
  }

  if (!(await throttle('register', 10))) {
    return {
      success: false,
      error: t(lang, 'Too many attempts. Please try again later.', 'Trop de tentatives. Réessayez plus tard.'),
    };
  }

  // An address that already has an account gets a note telling its owner to sign
  // in, and the form answers exactly as it would for a new address: the reply
  // must not reveal who is registered here.
  const existing = await findUserByEmail(cleanEmail);
  if (existing) {
    try {
      const appUrl = process.env.NEXT_PUBLIC_APP_URL || process.env.APP_URL || 'http://localhost:3000';
      await sendEmail({
        to: cleanEmail,
        subject: t(lang, 'You already have an account', 'Vous avez déjà un compte'),
        text: t(
          lang,
          `Someone tried to register with this address. You already have an account: sign in at ${appUrl}${langPrefix(lang)}/login. If this was not you, you can ignore this message.`,
          `Quelqu’un a tenté de s’inscrire avec cette adresse. Vous avez déjà un compte : connectez-vous sur ${appUrl}${langPrefix(lang)}/login. Si ce n’était pas vous, ignorez ce message.`
        ),
      });
    } catch (error) {
      console.error('Could not send the existing-account notice:', error);
    }
    return { success: false, needsVerification: true, email: cleanEmail };
  }

  const clip = (value: string | undefined, max: number) => (value || '').trim().slice(0, max) || null;
  const issued = await issueEmailCode(cleanEmail, lang, {
    password_hash: hashPassword(data.password),
    role: data.role === 'realtor' ? 'realtor' : 'user',
    name: clip(data.name, 255),
    phone: clip(data.phone, 50),
    city: clip(data.city, 100),
    province: clip(data.province, 100),
    binding: await startRegistrationBinding(),
  });

  return codeStepResult(lang, cleanEmail, issued);
}

/**
 * Change password for authenticated user
 */
export async function changePasswordAction(oldPassword: string, newPassword: string, lang = 'en') {
  if (typeof oldPassword !== 'string') oldPassword = '';
  if (typeof newPassword !== 'string') newPassword = '';
  const session = await getSession();
  if (!session || !session.userId) {
    return { success: false, error: 'Unauthorized' };
  }

  if (!newPassword || newPassword.length < MIN_PASSWORD_LENGTH) {
    return {
      success: false,
      error: t(
        lang,
        `New password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
        `Le nouveau mot de passe doit comporter au moins ${MIN_PASSWORD_LENGTH} caractères.`
      ),
    };
  }

  const userRes = await query(`SELECT ${USER_COLUMNS} FROM users WHERE id = $1`, [session.userId]);
  const user = userRes.rows[0];
  if (!user) {
    return { success: false, error: 'User not found' };
  }

  // Accounts without a password were verified by email code; they may set one directly.
  if (user.password_hash && !verifyPassword(oldPassword, user.password_hash)) {
    return { success: false, error: t(lang, 'Current password is incorrect.', "L'ancien mot de passe est incorrect.") };
  }

  if (user.password_hash && newPassword === oldPassword) {
    return { success: false, error: t(lang, 'Choose a new password that is different from the current one.', 'Choisissez un nouveau mot de passe différent de l’actuel.') };
  }

  const updated = await query(
    `UPDATE users SET password_hash = $1, session_version = session_version + 1 WHERE id = $2 RETURNING ${USER_COLUMNS}`,
    [hashPassword(newPassword), user.id]
  );
  // Other devices are signed out; this one gets a fresh session.
  await startUserSession(updated.rows[0]);

  return { success: true };
}

/**
 * Changing the address a person signs in with (an agency owner, manager or consultant had no way to).
 * Step 1: the current password and the new address; a code is sent to the NEW address, so the change
 * only happens if its owner confirms. Nothing changes until step 2.
 */
export async function requestEmailChangeAction(newEmailRaw: string, password: string, lang = 'en') {
  const session = await getSession();
  if (!session || !session.userId) return { success: false, error: 'Unauthorized' };
  if (!(await throttle('email-change', 6))) {
    return { success: false, error: t(lang, 'Too many attempts. Please try again later.', 'Trop de tentatives. Réessayez plus tard.') };
  }
  const user = (await query(`SELECT ${USER_COLUMNS} FROM users WHERE id = $1 AND deleted_at IS NULL`, [session.userId])).rows[0];
  if (!user) return { success: false, error: 'User not found' };
  if (user.password_hash && !verifyPassword(password, user.password_hash)) {
    return { success: false, error: t(lang, 'Current password is incorrect.', "Le mot de passe actuel est incorrect.") };
  }
  const email = normalizeEmail(newEmailRaw);
  if (!isValidEmail(email)) return { success: false, error: t(lang, 'Enter a valid email address.', 'Indiquez une adresse courriel valide.') };
  if (email === normalizeEmail(user.email)) {
    return { success: false, error: t(lang, 'This is already your sign-in address.', 'C’est déjà votre adresse de connexion.') };
  }
  if (await findUserByEmail(email)) {
    return { success: false, error: t(lang, 'This address is already used by another account.', 'Cette adresse est déjà utilisée par un autre compte.') };
  }
  const issued = await issueEmailCode(email, lang, { changeEmailFor: String(user.id) });
  if (!issued.ok) {
    return {
      success: false,
      error: issued.reason === 'cooldown'
        ? t(lang, `Please wait ${issued.cooldownSeconds} seconds before asking for another code.`, `Patientez ${issued.cooldownSeconds} secondes avant de demander un autre code.`)
        : t(lang, 'We could not send the code. Please try again in a few minutes.', "Nous n'avons pas pu envoyer le code. Réessayez dans quelques minutes."),
    };
  }
  return { success: true, email };
}

/** Step 2: the code from the new address. The old address is told, and the other devices are signed out. */
export async function confirmEmailChangeAction(newEmailRaw: string, code: string, lang = 'en') {
  const session = await getSession();
  if (!session || !session.userId) return { success: false, error: 'Unauthorized' };
  if (!(await throttle('email-change-confirm', 20))) {
    return { success: false, error: t(lang, 'Too many attempts. Please try again later.', 'Trop de tentatives. Réessayez plus tard.') };
  }
  const email = normalizeEmail(newEmailRaw);
  const consumed = await consumeEmailCode(email, code, null);
  if (!consumed.ok) {
    return {
      success: false,
      error: consumed.reason === 'expired'
        ? t(lang, 'This code has expired. Ask for a new one.', 'Ce code a expiré. Demandez-en un nouveau.')
        : consumed.reason === 'too_many_attempts'
          ? t(lang, 'Too many attempts. Ask for a new code.', 'Trop de tentatives. Demandez un nouveau code.')
          : t(lang, 'Incorrect code.', 'Code incorrect.'),
    };
  }
  // A code that was meant for something else (a sign-up of that address) changes nothing here.
  if (consumed.payload?.changeEmailFor !== String(session.userId)) {
    return { success: false, error: t(lang, 'Incorrect code.', 'Code incorrect.') };
  }
  const before = (await query(`SELECT email, name FROM users WHERE id = $1`, [session.userId])).rows[0];
  let updated;
  try {
    updated = await query(
      `UPDATE users SET email = $1, session_version = session_version + 1
       WHERE id = $2 AND deleted_at IS NULL AND NOT EXISTS (SELECT 1 FROM users o WHERE LOWER(o.email) = $1 AND o.id <> $2)
       RETURNING ${USER_COLUMNS}`,
      [email, session.userId]
    );
  } catch {
    updated = null;
  }
  if (!updated?.rows[0]) {
    return { success: false, error: t(lang, 'This address is already used by another account.', 'Cette adresse est déjà utilisée par un autre compte.') };
  }
  await startUserSession(updated.rows[0]);
  if (before?.email) {
    await sendEmail({
      to: before.email,
      subject: 'Your sign-in address was changed / Votre adresse de connexion a été modifiée',
      text: `The address you sign in with on Real Estate Channel is now ${email}. If this was not you, contact support right away.

L’adresse de connexion de votre compte Real Estate Channel est maintenant ${email}. Si ce n’était pas vous, contactez l’assistance sans tarder.`,
      html: `<p>The address you sign in with on Real Estate Channel is now <strong>${email.replace(/[<>&]/g, '')}</strong>. If this was not you, contact support right away.</p><p>L’adresse de connexion de votre compte Real Estate Channel est maintenant <strong>${email.replace(/[<>&]/g, '')}</strong>. Si ce n’était pas vous, contactez l’assistance sans tarder.</p>`,
    }).catch(() => {});
  }
  return { success: true, email };
}

/**
 * Signs the person out of every device except this one: the session version
 * moves on, so each older cookie stops matching, and this browser is given a
 * fresh session. For a lost phone or a shared computer, without a password change.
 */
export async function signOutEverywhereAction() {
  const session = await getSession();
  if (!session || !session.userId) {
    return { success: false, error: 'Unauthorized' };
  }
  const updated = await query(
    `UPDATE users SET session_version = session_version + 1 WHERE id = $1 AND deleted_at IS NULL RETURNING ${USER_COLUMNS}`,
    [session.userId]
  );
  if (!updated.rows[0]) {
    return { success: false, error: 'User not found' };
  }
  await startUserSession(updated.rows[0]);
  return { success: true };
}

/**
 * Reset password request (forgot password)
 */
export async function resetPasswordRequestAction(email: string, lang = 'en') {
  const startedAt = Date.now();
  const cleanEmail = normalizeEmail(email);
  if (!isValidEmail(cleanEmail)) {
    return { success: false, error: t(lang, 'Invalid email.', 'Courriel invalide.') };
  }

  const genericSuccess = {
    success: true,
    message: t(
      lang,
      'If this email exists in our system, reset instructions have been sent.',
      'Si cette adresse existe dans notre système, des instructions de réinitialisation ont été envoyées.'
    ),
  };

  // Each request sends mail to an address the requester typed, so it is capped
  // per connection: otherwise this is a way to flood a stranger's inbox.
  if (!(await throttle('reset', 10))) {
    return notFasterThan(startedAt, RESET_FLOOR_MS, genericSuccess);
  }

  // A site that cannot send mail says so, to everyone alike (the answer depends on the server's settings, not on the address), instead of
  // a "sent" that never arrives.
  // RESET_MAIL_FROM=support sends the reset link from the support@ mailbox instead of notreply@ (for a site where only that mailbox exists yet).
  const resetFrom = process.env.RESET_MAIL_FROM === 'support' ? 'support' : 'noreply';
  if (!(await mailConfigured(resetFrom))) {
    return {
      success: false,
      error: t(
        lang,
        'This site cannot send email yet, so no reset link can be sent. Whoever runs the site has to set up the mailbox first (SMTP settings on the server, then Admin > Mail).',
        "Ce site ne peut pas encore envoyer de courriels, aucun lien ne peut donc être envoyé. La personne qui gère le site doit d'abord configurer la boîte (paramètres SMTP du serveur, puis Admin > Mail)."
      ),
    };
  }

  const user = await findUserByEmail(cleanEmail);
  // Same response whether or not the account exists, so addresses cannot be
  // probed - and the same time, or the wording would not matter: a known
  // address took a row write and a message to the mail server longer.
  if (!user || user.is_blocked || user.deleted_at) {
    return notFasterThan(startedAt, RESET_FLOOR_MS, genericSuccess);
  }

  const rawResetToken = crypto.randomBytes(24).toString('hex');
  const hashedResetToken = crypto.createHash('sha256').update(rawResetToken).digest('hex');

  await query(
    `UPDATE users SET reset_token = $1, reset_token_expires_at = NOW() + INTERVAL '1 hour' WHERE id = $2`,
    [hashedResetToken, user.id]
  );

  const appUrl = process.env.NEXT_PUBLIC_APP_URL || process.env.APP_URL || 'http://localhost:3000';
  const resetUrl = `${appUrl}${langPrefix(lang === 'fr' ? 'fr' : 'en')}/restore-access?token=${rawResetToken}`;
  const subject = t(lang, 'Reset your password', 'Réinitialisation de votre mot de passe');
  const html = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 580px; margin: 0 auto; padding: 24px; background: #ffffff; border-radius: 16px; border: 1px solid #e5e5e5;">
      <h2 style="color: #171717; margin-top: 0;">${subject}</h2>
      <p style="color: #525252; font-size: 15px; line-height: 1.5;">
        ${t(
          lang,
          'You requested to reset your password. Click the button below to choose a new password:',
          'Vous avez demandé la réinitialisation de votre mot de passe. Cliquez sur le bouton ci-dessous pour en créer un nouveau :'
        )}
      </p>
      <div style="margin: 28px 0; text-align: center;">
        <a href="${resetUrl}" style="background-color: #171717; color: #ffffff; padding: 14px 28px; text-decoration: none; border-radius: 12px; font-weight: 600; display: inline-block; font-size: 15px;">
          ${t(lang, 'Set New Password', 'Définir un nouveau mot de passe')}
        </a>
      </div>
      <p style="color: #737373; font-size: 13px;">
        ${t(lang, 'This link will expire in 1 hour. If you did not ask for this, ignore this email: your password stays as it is.', 'Ce lien expirera dans 1 heure. Si vous n’avez rien demandé, ignorez ce courriel : votre mot de passe reste inchangé.')}
      </p>
    </div>
  `;

  const resetText = t(
    lang,
    `You requested to reset your password. Choose a new one here (the link expires in 1 hour): ${resetUrl}\n\nIf you did not ask for this, ignore this email: your password stays as it is.`,
    `Vous avez demandé la réinitialisation de votre mot de passe. Choisissez-en un nouveau ici (le lien expire dans 1 heure) : ${resetUrl}\n\nSi vous n’avez rien demandé, ignorez ce courriel : votre mot de passe reste inchangé.`
  );
  await sendEmail({ to: user.email, subject, text: resetText, html, from: resetFrom });
  return notFasterThan(startedAt, RESET_FLOOR_MS, genericSuccess);
}

/**
 * Confirm reset password with token
 */
/** Whether a reset link can still be used, asked when its page opens (nothing is changed or used up). */
export async function checkResetTokenAction(token: string): Promise<boolean> {
  const clean = String(token || '').trim();
  if (!clean || clean.length > 200) return false;
  const hashed = crypto.createHash('sha256').update(clean).digest('hex');
  const res = await query(`SELECT 1 FROM users WHERE reset_token = $1 AND reset_token_expires_at > NOW() AND is_blocked IS NOT TRUE`, [hashed]);
  return res.rows.length > 0;
}

export async function resetPasswordConfirmAction(token: string, newPassword: string, lang = 'en') {
  // Server actions take whatever the browser sends: anything but text is the same as nothing.
  if (typeof token !== 'string') token = '';
  if (typeof newPassword !== 'string') newPassword = '';
  if (!token || !newPassword || newPassword.length < MIN_PASSWORD_LENGTH) {
    return {
      success: false,
      error: t(
        lang,
        `Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`,
        `Le mot de passe doit comporter au moins ${MIN_PASSWORD_LENGTH} caractères.`
      ),
    };
  }

  const hashedToken = crypto.createHash('sha256').update(token.trim()).digest('hex');
  const userRes = await query(
    `UPDATE users
     SET password_hash = $2,
         reset_token = NULL,
         reset_token_expires_at = NULL,
         failed_login_count = 0,
         locked_until = NULL,
         session_version = session_version + 1
     WHERE reset_token = $1 AND reset_token_expires_at > NOW() AND is_blocked IS NOT TRUE
     RETURNING ${USER_COLUMNS}`,
    [hashedToken, hashPassword(newPassword)]
  );

  const user = userRes.rows[0];
  if (!user) {
    return {
      success: false,
      error: t(lang, 'Reset link is invalid or has expired.', 'Lien de réinitialisation expiré ou invalide.'),
    };
  }

  const result = await signedInResult(user);
  return result.success
    ? { success: true, role: result.role, is_realtor: result.is_realtor, agency_status: result.agency_status, agency_member: result.agency_member }
    : { success: false, error: 'Unable to sign in' };
}

/**
 * Administrator sign-in. Each administrator has their own account, so the audit
 * trail names a person and access can be withdrawn by disabling that account.
 * The ADMIN_PASSWORD from the environment stays as a way back in if every admin
 * account is locked out; it is only accepted when no admin account exists for
 * that address.
 */
export async function adminLoginAction(password: string, email?: string) {
  if (!(await throttle('admin-login', 20))) {
    return { success: false, error: 'Too many attempts. Try again later.' };
  }

  const cleanEmail = normalizeEmail(email || 'admin@realestatechannel.ca');
  const user = await findUserByEmail(cleanEmail);

  if (user && user.role === 'admin') {
    if (user.is_blocked || user.deleted_at) {
      return { success: false, error: 'This administrator account is disabled.' };
    }
    if (user.locked_until && new Date(user.locked_until).getTime() > Date.now()) {
      return { success: false, error: 'Too many failed attempts. Try again in a few minutes.' };
    }
    if (!user.password_hash || !verifyPassword(password || '', user.password_hash)) {
      await recordFailedLogin(user.id);
      return { success: false, error: 'Invalid email or password' };
    }
    await query(`UPDATE users SET failed_login_count = 0, locked_until = NULL WHERE id = $1`, [user.id]);
    await setSessionCookie({
      userId: user.id,
      email: user.email,
      name: user.name || 'Administrator',
      role: 'admin',
      sv: Number(user.session_version || 0),
    }, 7);
    return { success: true };
  }

  // No admin account with that address: fall back to the environment password, and
  // only for its own address. The session it opens names that address, not whatever
  // was typed into the form.
  if (user || cleanEmail !== ENVIRONMENT_ADMIN_EMAIL) {
    return { success: false, error: 'Invalid email or password' };
  }

  const check = await checkAdminPassword(password || '');
  if (check === 'ok') {
    await setSessionCookie({ email: cleanEmail, role: 'admin', name: 'System Administrator' }, 7);
    return { success: true };
  }
  if (check === 'disabled') {
    return { success: false, error: 'Admin sign-in is disabled: no administrator account exists and ADMIN_PASSWORD is not configured.' };
  }
  if (check === 'locked') {
    return { success: false, error: 'Too many attempts. Try again in 15 minutes.' };
  }
  return { success: false, error: 'Invalid email or password' };
}

/**
 * Accepts an invitation to an agency team. The invitee chooses their own
 * password here, so it never travels by email and the person who invited them
 * never learns it.
 */
export async function acceptAgencyInvite(token: string, password: string, lang = 'en') {
  const cleanToken = String(token || '').trim();
  if (!cleanToken) {
    return { success: false, error: t(lang, 'This invitation link is invalid.', "Ce lien d'invitation est invalide.") };
  }
  if (!(await throttle('join-agency', 20))) {
    return { success: false, error: t(lang, 'Too many attempts. Try again later.', 'Trop de tentatives. Réessayez plus tard.') };
  }

  const tokenHash = crypto.createHash('sha256').update(cleanToken).digest('hex');
  const inviteRes = await query(
    `SELECT i.id, i.agency_id, i.email, i.role, i.title
     FROM agency_invites i
     JOIN users a ON a.id = i.agency_id
     WHERE i.token_hash = $1 AND i.accepted_at IS NULL AND i.expires_at > NOW()
       AND a.is_realtor = true AND a.deleted_at IS NULL AND a.is_blocked IS NOT TRUE`,
    [tokenHash]
  );
  const invite = inviteRes.rows[0];
  if (!invite || !isMemberRole(invite.role)) {
    return { success: false, error: t(lang, 'This invitation has expired or was withdrawn.', 'Cette invitation a expiré ou a été retirée.') };
  }

  const email = normalizeEmail(invite.email);
  let user = await findUserByEmail(email);

  if (user) {
    if (user.is_blocked || user.deleted_at) {
      return { success: false, error: t(lang, 'This account cannot be used.', 'Ce compte ne peut pas être utilisé.') };
    }
    // An agency account cannot also be somebody else's employee.
    if (user.is_realtor) {
      return { success: false, error: t(lang, 'This address already runs its own agency.', "Cette adresse gère déjà sa propre agence.") };
    }
    const alreadyIn = await query(`SELECT agency_id FROM agency_members WHERE user_id = $1`, [user.id]);
    if (alreadyIn.rows[0] && alreadyIn.rows[0].agency_id !== invite.agency_id) {
      return { success: false, error: t(lang, 'This address already belongs to another agency.', 'Cette adresse appartient déjà à une autre agence.') };
    }
    // An administrator account is not a member of an agency's team.
    if (user.role === 'admin') {
      return { success: false, error: t(lang, 'This address cannot join a team.', 'Cette adresse ne peut pas rejoindre une équipe.') };
    }
    // An account that has no password yet sets one from the invitation.
    if (!user.password_hash) {
      if (!password || password.length < MIN_PASSWORD_LENGTH) {
        return {
          success: false,
          error: t(lang, `Choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`, `Choisissez un mot de passe d'au moins ${MIN_PASSWORD_LENGTH} caractères.`),
        };
      }
      const updated = await query(
        `UPDATE users SET password_hash = $2, session_version = session_version + 1 WHERE id = $1 RETURNING ${USER_COLUMNS}`,
        [user.id, hashPassword(password)]
      );
      user = updated.rows[0];
    }
  } else {
    if (!password || password.length < MIN_PASSWORD_LENGTH) {
      return {
        success: false,
        error: t(lang, `Choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`, `Choisissez un mot de passe d'au moins ${MIN_PASSWORD_LENGTH} caractères.`),
      };
    }
    const inserted = await query(
      `INSERT INTO users (email, password_hash, role, is_realtor, agency_status, created_at)
       VALUES ($1, $2, 'user', false, 'approved', NOW())
       RETURNING ${USER_COLUMNS}`,
      [email, hashPassword(password)]
    );
    user = inserted.rows[0];
  }

  // Claiming the invitation and joining are one unit: of two requests with the same
  // link sent together, one finds it unused, and the other is told it has expired.
  const joined = await transaction(async (exec) => {
    const claimed = await exec(
      `UPDATE agency_invites SET accepted_at = NOW() WHERE id = $1 AND accepted_at IS NULL RETURNING id`,
      [invite.id]
    );
    if (!claimed.rows[0]) return false;
    await exec(
      `INSERT INTO agency_members (agency_id, user_id, role, title)
       VALUES ($1, $2, $3, $4)
       ON CONFLICT (agency_id, user_id) DO UPDATE SET role = EXCLUDED.role, title = EXCLUDED.title`,
      [invite.agency_id, user.id, invite.role, invite.title]
    );
    return true;
  });
  if (!joined) {
    return { success: false, error: t(lang, 'This invitation has expired or was withdrawn.', 'Cette invitation a expiré ou a été retirée.') };
  }

  await startUserSession(user);
  revalidatePath('/[lang]/cabinet/realtor/settings', 'page');
  return { success: true, role: invite.role as MemberRole };
}

export async function deleteUserAccountServer() {
  try {
    const session = await getSession();
    if (!session || !session.userId) {
      return { success: false, error: 'Unauthorized: Session required' };
    }
    const userId = session.userId;

    // Note every upload this person owns BEFORE the rows go, because afterwards
    // there is nothing left to say which files were theirs.
    const owned = await query<{ url: string | null }>(
      `SELECT ph.url FROM photos ph JOIN properties p ON p.id = ph.property_id WHERE p.user_id = $1
       UNION ALL SELECT m.url FROM media m WHERE m.owner_id = $1
       UNION ALL SELECT unnest(p.photos) FROM properties p WHERE p.user_id = $1
       UNION ALL SELECT u.avatar_url FROM users u WHERE u.id = $1
       UNION ALL SELECT u.banner_url FROM users u WHERE u.id = $1
       UNION ALL SELECT u.logo_url FROM users u WHERE u.id = $1`,
      [userId]
    );
    const person = await query<{ email: string | null; specialists: unknown }>(
      `SELECT email, specialists FROM users WHERE id = $1`,
      [userId]
    );
    const email = person.rows[0]?.email || null;
    const specialistUrls = JSON.stringify(person.rows[0]?.specialists ?? '')
      .match(/\/uploads\/[A-Za-z0-9_-]+\.[a-z]+/g) || [];

    // A-16. Read before the delete; told after it has committed.
    const waiting = await findWaitingBuyers(userId, email);

    // The people on this agency's team lose their access with it. Read before the
    // rows go, told after the delete has committed.
    const team = await query<{ id: string; email: string | null }>(
      `SELECT u.id, u.email FROM agency_members m JOIN users u ON u.id = m.user_id WHERE m.agency_id = $1 AND u.deleted_at IS NULL`,
      [userId]
    );
    const agencyName = (await query<{ name: string | null }>(`SELECT name FROM users WHERE id = $1`, [userId])).rows[0]?.name || 'The agency';

    // All or nothing: a failure half-way used to leave the listings gone and
    // the account still there, or the reverse.
    await transaction(async (exec) => {
      await exec(`DELETE FROM complaints WHERE property_id IN (SELECT id FROM properties WHERE user_id = $1)`, [userId]);
      await exec(`DELETE FROM photos WHERE property_id IN (SELECT id FROM properties WHERE user_id = $1)`, [userId]);
      await exec(`DELETE FROM properties WHERE user_id = $1`, [userId]);
      await exec(`DELETE FROM users WHERE id = $1`, [userId]);
      // Their team role goes with the agency: they are ordinary accounts again.
      if (team.rows.length) {
        await exec(`UPDATE users SET role = 'user' WHERE id = ANY($1::uuid[]) AND role = 'realtor' AND is_realtor IS NOT TRUE`, [team.rows.map((m) => m.id)]);
      }
      // A search saved without an account is keyed by the address alone, so the
      // cascade from users never reaches it. Left behind, it would keep emailing
      // someone who asked to be deleted.
      if (email) {
        await exec(`DELETE FROM saved_searches WHERE LOWER(email) = LOWER($1)`, [email]);
        // The notices written for them, keyed by address too, would greet whoever registers it next.
        await exec(`DELETE FROM user_notifications WHERE LOWER(user_email) = LOWER($1)`, [email]);
      }
    });

    // Enquiries this person sent about other people's listings stay: they
    // belong to the seller who received them as much as to the sender.

    await tellBuyersListingWithdrawn(waiting);
    for (const member of team.rows) {
      if (!member.email) continue;
      try {
        const { sendAgencyClosedEmail } = await import('@/lib/agency-emails');
        await sendAgencyClosedEmail({ to: member.email, agencyName });
      } catch (mailError) {
        console.error('Could not tell a team member the agency was closed:', mailError);
      }
    }

    // The files last, once nothing points at them. Photos someone else also
    // uses (the same upload can be shared) stay.
    await removeUnreferencedUploads([...owned.rows.map((r) => r.url), ...specialistUrls]);

    await clearSessionCookie();
    return { success: true };
  } catch (error: any) {
    console.error('Error deleting user account:', error);
    return { success: false, error: publicError(error) };
  }
}

export async function upgradeUserToAgencyServer(agencyName?: string) {
  try {
    const session = await getSession();
    if (!session || !session.userId) {
      return { success: false, error: 'Unauthorized: Session required' };
    }

    // Somebody on another agency's team belongs to one agency only: they would
    // become a second agency while still holding the first one's access.
    const onTeam = await query(`SELECT 1 FROM agency_members WHERE user_id = $1 LIMIT 1`, [session.userId]);
    if (onTeam.rows[0]) {
      return { success: false, error: 'You are on an agency team. Leave it before opening an agency of your own.' };
    }

    const name = agencyName?.trim().slice(0, 255) || session.name || 'Real Estate Agency';

    // A converted account starts as an agency awaiting review, like a new registration.
    const agencySlug = await generateAgencySlug(name, session.email.split('@')[0]);
    const res = await query(
      `UPDATE users
       SET is_realtor = TRUE,
           role = 'realtor',
           agency_status = CASE WHEN is_realtor IS TRUE THEN agency_status ELSE 'pending' END,
           agency_slug = COALESCE(agency_slug, $3),
           name = COALESCE(NULLIF($1, ''), name)
       WHERE id = $2
       RETURNING ${USER_COLUMNS}`,
      [name, session.userId, agencySlug]
    );

    const updatedUser = res.rows[0];
    if (!updatedUser) {
      return { success: false, error: 'User not found' };
    }
    await startUserSession(updatedUser);

    return {
      success: true,
      user: {
        id: updatedUser.id,
        email: updatedUser.email,
        name: updatedUser.name,
        role: updatedUser.role,
        is_realtor: updatedUser.is_realtor,
      },
    };
  } catch (error: any) {
    console.error('Error upgrading user to agency:', error);
    return { success: false, error: publicError(error) };
  }
}
